import { useEffect, useRef, useState, useCallback } from "react";

// Create a global event emitter for data refresh
class RefreshEmitter {
  constructor() {
    this.listeners = new Map();
  }

  on(dataType, callback) {
    if (!this.listeners.has(dataType)) {
      this.listeners.set(dataType, []);
    }
    this.listeners.get(dataType).push(callback);
  }

  off(dataType, callback) {
    if (!this.listeners.has(dataType)) return;
    const callbacks = this.listeners.get(dataType);
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
  }

  emit(dataType) {
    if (!this.listeners.has(dataType)) return;
    this.listeners.get(dataType).forEach((callback) => callback());
  }
}

export const refreshEmitter = new RefreshEmitter();

/**
 * Custom hook for fetching data with auto-refresh capability
 * @param {Function} fetchFn - Function that returns a promise
 * @param {string} dataType - Type of data (e.g., 'gallery', 'notices', 'events')
 * @param {Object} options - Configuration options
 * @returns {Object} { data, loading, error, refetch }
 */
export const useDataRefresh = (fetchFn, dataType, options = {}) => {
  const {
    pollInterval = null, // Set to a number (ms) for polling, null for no polling
    dependencies = [],
  } = options;

  const [state, setState] = useState({
    data: [],
    loading: true,
    error: null,
  });

  const hasLoadedRef = useRef(false);

  // Refetch function. Only the first load shows a loading state; later refreshes
  // (polling, refresh events) are silent and keep the previous data reference when
  // nothing changed, so pages don't flash a spinner or reset expanded items/pagination.
  const refetch = useCallback(async () => {
    if (!hasLoadedRef.current) {
      setState((prev) => ({ ...prev, loading: true }));
    }
    try {
      const result = await fetchFn();
      hasLoadedRef.current = true;
      setState((prev) =>
        !prev.loading &&
        !prev.error &&
        JSON.stringify(prev.data) === JSON.stringify(result)
          ? prev
          : { data: result, loading: false, error: null },
      );
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  }, [fetchFn]);

  // Initial fetch
  useEffect(() => {
    refetch();
  }, dependencies);

  // Set up polling
  useEffect(() => {
    if (!pollInterval) return;
    const interval = setInterval(refetch, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval, refetch]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      refetch();
    };

    refreshEmitter.on(dataType, handleRefresh);
    return () => {
      refreshEmitter.off(dataType, handleRefresh);
    };
  }, [dataType, refetch]);

  return state;
};

/**
 * Trigger a refresh for a specific data type across all components
 * @param {string} dataType - Type of data to refresh
 */
export const triggerRefresh = (dataType) => {
  refreshEmitter.emit(dataType);
};
